import { border, motion, space, typography } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { RequiredThemeKeys, themed } from "@smartsheet/sds-theme-internal";
import { ANIMATION_DURATION } from "./tooltipConstants";

export const tooltipTheme = themed({
    background: {
        "lodestar-light": color("color--background--spectrum-slate--strongest"),
        "ursa-light": color("color--background--primary--strongest"),
        "ursa-dark": color("color--background--primary--weakest"),
    },
    borderRadius: {
        "lodestar-light": border.radius05,
        "ursa-light": border.radius20,
    },
    textColor: {
        body: color("color--text--primary--weakest"),
        title: color("color--text--primary--weakest"),
    },
    fontSize: {
        // Yay these are all the same.
        basic: typography.fontSize.size20,
        multiLineTitle: {
            "lodestar-light": "13px",
            "ursa-light": typography.fontSize.size30,
        },
        multiLine: {
            "lodestar-light": "13px",
            "ursa-light": typography.fontSize.size20,
        },
        multilineTitleDeprecated: {
            "lodestar-light": "13px",
            "ursa-light": typography.fontSize.size30,
        },
    },
    fontWeight: {
        body: typography.fontWeight.regular,
        title: typography.fontWeight.semiBold,
    },
    lineHeight: {
        body: {
            "lodestar-light": typography.lineHeight.size30,
            "ursa-light": typography.lineHeight.size20,
        },
        title: typography.lineHeight.size30,
    },
    padding: {
        body: {
            "lodestar-light": "3px 8px",
            "ursa-light": space.space30,
        },
        // These styles are from the old Lodestar version that didn't use the TooltipBody so these
        // components provided all the necessary padding.
        multiLineDeprecated: {
            title: {
                "lodestar-light": `${space.space20} ${space.space40} ${space.space10}`,
                "ursa-light": `${space.space30} ${space.space30} ${space.space10}`,
            },
            subtext: {
                "lodestar-light": `0 ${space.space40} ${space.space30}`,
                "ursa-light": `0 ${space.space30} ${space.space30}`,
            },
            subtextShift: {
                "lodestar-light": "-8px",
                "ursa-light": "-4px",
            },
        },
        // These styles require the TooltipBody to wrap the components that use them.
        multiLine: {
            title: {
                "lodestar-light": `9px ${space.space20} ${space.space10}`,
                "ursa-light": `0 0 ${space.space10}`,
            },
            text: {
                "lodestar-light": `0 ${space.space20}`,
                "ursa-light": `0`,
            },
            // The distance between tooltip text siblings. The title has the gap between title and
            // text.
            textGap: {
                "lodestar-light": space.space10,
                "ursa-light": space.space20,
            },
            textEndGap: {
                // This is the special gap added to get the right padding-bottom in the Lodestar
                // version of the multiline tooltip.
                "lodestar-light": "9px",
                "ursa-light": "0",
            },
        },
    },
    motion: {
        slide: {
            "lodestar-light": `${ANIMATION_DURATION}ms cubic-bezier(0.23, 1, 0.32, 1)`,
            "ursa-light": motion.animation.default,
        },
        opacity: {
            "lodestar-light": `${ANIMATION_DURATION / 2}ms`,
            "ursa-light": motion.animation.default,
        },
    },
});

export const getTooltipMultilineTitleTypeRampProps = (themeMode: RequiredThemeKeys) => {
    switch (themeMode) {
        case "ursa-light":
        case "ursa-dark":
            return {
                system: "ursa",
                variant: "Inter--14px--600w",
            } as const;
        case "lodestar-light":
        case "oldestar":
        default:
            return {
                system: "lodestar",
                variant: "headerSmall",
            } as const;
    }
};
